<template>
  <div class="followup-tab p-mt-3">
    <div v-if="loading && !data" class="p-text-center"><ProgressSpinner /></div>

    <div v-else-if="loadError" class="p-invalid">{{ loadError }}</div>

    <div v-else-if="data">
      <div v-if="!data.quoteVerId" class="p-mb-3">This enquiry has no quote yet, so there is nothing to follow up.</div>

      <div v-else>
        <div class="p-d-flex p-ai-center p-flex-wrap p-mb-3">
          <h5 class="followup-heading">Follow-up emails for {{ data.quoteRef }}</h5>
          <span id="followupState" class="followup-state">{{ stateLabel(followup && followup.state) }}</span>
          <span v-if="followup && followup.needsAttention" id="needsAttention" class="needs-attention">Needs attention</span>
        </div>

        <div v-if="uncertain" id="uncertainWarning" class="uncertain-warning p-mb-3">{{ uncertainText }}</div>

        <div class="p-grid">
          <div class="p-col-12 p-md-6">
            <table class="followup-facts">
              <tr><th>Quote status</th><td>{{ data.quoteStatus }}</td></tr>
              <template v-if="followup">
                <tr><th>Next email</th><td id="nextSendAt">{{ followup.nextSendAt || 'None due' }}</td></tr>
                <tr><th>Steps sent</th><td>{{ followup.lastStepSent || 0 }}</td></tr>
                <tr v-if="followup.lastSentAt"><th>Last sent</th><td>{{ followup.lastSentAt }}</td></tr>
                <tr><th>Enrolled</th><td>{{ followup.enrolledAt }}<span v-if="followup.enrolledBy"> by {{ followup.enrolledBy }}</span><span v-if="followup.manual"> (by hand)</span></td></tr>
                <tr v-if="followup.approvedAt"><th>Approved</th><td>{{ followup.approvedAt }}<span v-if="followup.approvedBy"> by {{ followup.approvedBy }}</span></td></tr>
                <tr v-if="followup.salespersonName"><th>Salesperson</th><td>{{ followup.salespersonName }}</td></tr>
                <tr v-if="followup.stopReason"><th>Why it stopped</th><td id="stopReason">{{ stopReasonLabel(followup.stopReason) }}</td></tr>
                <tr v-if="followup.lastError"><th>Last error</th><td>{{ followup.lastError }}</td></tr>
              </template>
              <tr v-if="notEmailedReasons.length">
                <th>{{ followup ? 'Will stop at the next send because' : 'Not emailed because' }}</th>
                <td id="notEmailedReasons"><div v-for="r in notEmailedReasons" :key="r">{{ r }}</div></td>
              </tr>
            </table>
          </div>

          <div class="p-col-12 p-md-6">
            <div class="p-d-flex p-flex-wrap p-ai-center">
              <Button
                v-for="a in actions"
                :key="a"
                :id="'followup-' + a"
                :label="actionLabels[a]"
                :class="['p-mr-2', 'p-mb-2', a === 'stop' ? 'p-button-danger' : '']"
                :disabled="busy"
                @click="act(a)"
              />
            </div>
            <p v-if="!followup && data.askReenrol" id="askReenrolNote" class="hint">
              Follow-up emails were stopped by hand on the previous version.
            </p>
            <p v-if="!data.canAct" class="hint">Only the quote's salesperson or a settings user can change follow-ups.</p>
          </div>
        </div>

        <div v-if="data.preview" class="p-mt-3" id="followupPreview">
          <h6>Next email (step {{ data.preview.stepNo }}, {{ data.preview.daysAfterEnrolment }} days after the quote is booked)</h6>
          <div class="preview">
            <div id="previewTo"><strong>To:</strong> {{ data.preview.to }}</div>
            <div v-if="data.preview.deliveryNote" id="deliveryNote" class="delivery-note">{{ data.preview.deliveryNote }}</div>
            <div><strong>Subject:</strong> {{ data.preview.subject }}</div>
            <pre class="preview-body">{{ data.preview.body }}</pre>
          </div>
        </div>

        <Dialog header="Check first" :visible.sync="confirmVisible" :style="{ width: '420px' }" :modal="true">
          <p id="followupConfirmText">{{ confirmText }}</p>
          <template #footer>
            <Button id="followupConfirmNo" label="No" icon="pi pi-times" class="p-button-text" @click="confirmVisible = false" />
            <Button id="followupConfirmYes" label="Yes" icon="pi pi-check" class="p-button-text" :disabled="busy" @click="confirmResolve" />
          </template>
        </Dialog>

        <div class="p-mt-3">
          <h6>Emails sent</h6>
          <DataTable :value="data.log || []" class="p-datatable-striped p-datatable-sm p-datatable-gridlines" dataKey="id">
            <template #empty>No follow-up emails sent yet.</template>
            <Column field="stepNo" header="Step" headerStyle="width: 11em">
              <template #body="slotProps">{{ logStepLabel(slotProps.data.stepNo) }}</template>
            </Column>
            <Column field="status" header="Result" headerStyle="width: 12em">
              <template #body="slotProps">{{ logStatusLabel(slotProps.data.status) }}</template>
            </Column>
            <Column field="sentAt" header="When" headerStyle="width: 10em" />
            <Column field="actualTo" header="Sent to">
              <template #body="slotProps">
                {{ slotProps.data.actualTo }}
                <small v-if="slotProps.data.intendedTo && slotProps.data.intendedTo !== slotProps.data.actualTo" class="hint">
                  (meant for {{ slotProps.data.intendedTo }})
                </small>
              </template>
            </Column>
            <Column field="subject" header="Subject" />
            <Column field="error" header="Error" />
          </DataTable>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FollowupService, { errorMessage } from '@/service/FollowupService'
import EventBus from '@/event-bus'
import { FOLLOWUP_CHANGED } from '@/mbp/universalComponents/quotation/followupRevision'
import {
  stateLabel, stopReasonLabel, actionsFor, ACTION_LABELS, logStatusLabel, logStepLabel, uncertainEmail, uncertainWarning
} from './followupState'

const DONE = {
  enrol: 'Follow-up emails started for this quote.',
  stop: 'Follow-up emails stopped.',
  reenable: 'Follow-up emails re-enabled.',
  clearAttention: 'Marked as dealt with.',
  resolveSent: 'Recorded as sent; follow-up emails carry on from the next step.',
  resolveResend: 'It will be sent again at the next sending time.'
}

export default {
  props: ['opportunity'],
  data: () => ({
    followupService: new FollowupService(),
    data: null,
    loading: false,
    loadError: '',
    busy: false,
    actionLabels: ACTION_LABELS,
    confirmVisible: false,
    pendingResolve: null
  }),
  computed: {
    oppId() {
      return (this.opportunity && this.opportunity.oppId) || this.$route.params.custid
    },
    followup() {
      return this.data ? this.data.followup : null
    },
    notEmailedReasons() {
      return (this.data && this.data.notEmailedReasons) || []
    },
    uncertain() {
      var f = this.followup
      return !!f && f.uncertainStepNo !== null && f.uncertainStepNo !== undefined
    },
    uncertainText() {
      return this.uncertain ? uncertainWarning(this.followup.uncertainStepNo) : ''
    },
    confirmText() {
      if (!this.uncertain) return ''
      var which = uncertainEmail(this.followup.uncertainStepNo)
      return this.pendingResolve === 'resolveSent'
        ? 'You found ' + which + ' in the sent folder of the CRM mailbox, so it will be recorded as sent and not sent again. Carry on?'
        : 'You could not find ' + which + ' in the sent folder of the CRM mailbox, so it will be sent again. Carry on?'
    },
    actions() {
      if (!this.data) return []
      return actionsFor(this.followup, this.data.canAct, !!this.data.quoteVerId)
    }
  },
  created() {
    EventBus.$on(FOLLOWUP_CHANGED, this.onFollowupChanged)
    this.load()
  },
  beforeDestroy() {
    EventBus.$off(FOLLOWUP_CHANGED, this.onFollowupChanged)
  },
  methods: {
    stateLabel,
    stopReasonLabel,
    logStepLabel,
    logStatusLabel,
    load() {
      this.loading = true
      return this.followupService.byOpp(this.oppId)
        .then(data => {
          this.data = data
          this.loadError = ''
        })
        .catch(err => {
          this.loadError = errorMessage(err, 'The follow-up details could not be loaded.')
        })
        .finally(() => { this.loading = false })
    },
    // D11's "Yes" (FollowupRevisionDialog) enrols from outside this tab
    onFollowupChanged(payload) {
      if (payload && String(payload.oppId) === String(this.oppId)) this.load()
    },
    act(action) {
      // It was sent / Send it again: asked first, in a dialog (never a browser confirm)
      if (action === 'resolveSent' || action === 'resolveResend') {
        this.pendingResolve = action
        this.confirmVisible = true
        return Promise.resolve()
      }
      return this.run(action)
    },
    confirmResolve() {
      var action = this.pendingResolve
      this.confirmVisible = false
      this.pendingResolve = null
      return action ? this.run(action) : Promise.resolve()
    },
    run(action) {
      var call
      if (action === 'enrol') call = this.followupService.enrol(this.data.quoteVerId)
      else if (action === 'resolveSent' || action === 'resolveResend')
        call = this.followupService.resolveUncertain(this.followup.id, action === 'resolveSent')
      else call = this.followupService[action](this.followup.id)
      this.busy = true
      return call
        .then(() => {
          this.$toast.add({ severity: 'success', summary: ACTION_LABELS[action], detail: DONE[action], life: 5000 })
          return this.load()
        })
        .catch(err => {
          var body = err && err.response && err.response.data
          var detail = errorMessage(err, (body && body.result) || 'That did not work. Please try again.')
          this.$toast.add({ severity: 'error', summary: ACTION_LABELS[action], detail: detail, life: 5000 })
          return this.load()
        })
        .finally(() => { this.busy = false })
    }
  }
}
</script>

<style scoped>
/* PrimeFlex 2.0.0-rc.1 has no spacing classes (p-mr-3 etc.), so margins live here */
.followup-heading { margin: 0; }
.followup-state {
  margin-left: 0.75rem;
  background: #e3f2fd;
  color: #1565c0;
  border-radius: 3px;
  padding: 0.15rem 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
}
.needs-attention {
  margin-left: 0.5rem;
  background: #d32f2f;
  color: #fff;
  border-radius: 3px;
  padding: 0.15rem 0.5rem;
  font-size: 0.85rem;
}
.followup-facts th { text-align: left; padding-right: 1rem; font-weight: 600; vertical-align: top; }
.followup-facts td { padding-bottom: 0.25rem; }
.hint { color: #6c757d; }
.uncertain-warning {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 3px;
  padding: 0.5rem 0.75rem;
}
.preview { border: 1px solid #dee2e6; border-radius: 3px; padding: 0.75rem; }
.delivery-note { color: #b26a00; font-style: italic; font-size: 0.9rem; }
.preview-body { white-space: pre-wrap; font-family: inherit; margin: 0.5rem 0 0; }
</style>
